/**
 * The Assistant sidebar's Testing pieces (task 1188), rendered by
 * `AssistantTurnView` beside the cards the sidebar already has.
 *
 * 1. `AssistantCriteriaCard` — what a `criteria` turn saved (the Criteria
 *    Composer, task 1187): "N criteria saved · via Criteria Composer", then
 *    each criterion's name, one-line definition, required chip and the
 *    rationale naming the instruction it came from. Everything shown arrived
 *    with the turn (`criteria-composer-save`'s reply); it costs no request.
 * 2. `RunTheseTestsAction` — the test-turn card's next step: one test run of
 *    the tests the turn saved, on the working state, through the same creation
 *    path the Testing tab's run toolbar uses, then the Testing tab's Test runs
 *    section with the new test run open. Once started, the action becomes a
 *    quiet View run link, so one turn does not start the same test run twice.
 * 3. `MoreTestsOffer` — "Want more?" under that action (task 1204): two chips
 *    that send a fixed request for three more tests as the User's next turn in
 *    the same thread.
 *
 * All three answer to `innovate.fe.assistant.testingCards` (task 1215) and
 * render nothing with it off; the turn's scenarios list stays.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckIcon, PlayIcon } from '@heroicons/react/24/outline';
import { useAssistant } from '@/providers/AssistantProvider';
import { useFlag } from '@/hooks/useFeatureFlags';
import { SENT_STATE_MS } from '@/hooks/useAssistantHandoff';
import { useCreateEndpointTestRunGroup } from '@/hooks/useEndpointTestRunGroups';
import { useOptionalToast } from '@/contexts/ToastContext';
import { describeDefinition } from '@/components/endpoints/tests/CriteriaEditor';
import { IN_FLIGHT_TOAST_TITLE, inFlightRefusal, inFlightToastDetail } from '@/utils/apiRefusal';
import { useCurrentDeployments, useEnvironments } from '@/hooks/useEnvironments';
import { useResourceVersionStatus } from '@/hooks/useResourceVersions';
import { NOTHING_RELEASED, runTargetState } from '@/components/endpoints/tests/RunOptionsControls';
import { SPECIALIST_LABELS } from '@/types/assistant';
import type { AssistantSavedCriterion } from '@/types/assistant';

/** The Testing tab's Test runs section with one test run open (tasks 1185, 1188). */
export const testRunSectionPath = (endpointId: string, testRunId: string) =>
  `/services/${endpointId}?tab=testing&section=runs&testRun=${testRunId}`;

export function AssistantCriteriaCard({ criteria }: { criteria: AssistantSavedCriterion[] }) {
  const testingCardsEnabled = useFlag('innovate.fe.assistant.testingCards');
  if (!testingCardsEnabled) return null;
  return (
    <div data-testid="assistant-criteria-card" className="rounded-lg border border-gray-200 p-2 dark:border-gray-700">
      <p className="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
        {criteria.length} {criteria.length === 1 ? 'criterion' : 'criteria'} saved · via{' '}
        {SPECIALIST_LABELS['criteria-composer']}
      </p>
      <ul className="space-y-1.5">
        {criteria.map((criterion) => (
          <li key={criterion.id ?? criterion.name} data-testid="assistant-criterion" className="text-xs">
            <p className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-gray-800 dark:text-gray-200">{criterion.name}</span>
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                  criterion.required
                    ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {criterion.required ? 'required' : 'informational'}
              </span>
            </p>
            <p className="text-gray-600 dark:text-gray-400">{describeDefinition(criterion)}</p>
            {criterion.rationale && (
              <p className="italic text-gray-500 dark:text-gray-400">{criterion.rationale}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The test run each turn started, by turn id, for this session — so a sidebar
 * that remounts (closed and reopened, a resumed thread) still shows View run
 * rather than offering to start the same test run again. A replayed transcript
 * from an earlier session offers the action afresh.
 */
const startedTestRuns = new Map<string, string>();

interface RunTheseTestsActionProps {
  turnId: string;
  endpointId: string;
  testIds: string[];
}

export function RunTheseTestsAction(props: RunTheseTestsActionProps) {
  const testingCardsEnabled = useFlag('innovate.fe.assistant.testingCards');
  return testingCardsEnabled ? <RunTheseTestsActionContent {...props} /> : null;
}

function RunTheseTestsActionContent({ turnId, endpointId, testIds }: RunTheseTestsActionProps) {
  const createTestRun = useCreateEndpointTestRunGroup();
  const toast = useOptionalToast();
  const navigate = useNavigate();
  const [testRunId, setTestRunId] = useState<string | undefined>(() => startedTestRuns.get(turnId));
  /**
   * Where it runs (task 1236): the Testing tab's three-state rule, without a
   * picker — the card cannot ask. Before the first publish (or without
   * Publishing & versions) the working state; once published, the default
   * target, the `/` release or the built-in default, named under the button.
   * With nothing released the button is disabled and says why.
   */
  const versioningFlag = useFlag('innovate.fe.resource-versioning');
  const { status } = useResourceVersionStatus('endpoint', endpointId);
  const { environments } = useEnvironments({ enabled: status.versioningEnabled });
  const { data: deployments } = useCurrentDeployments(endpointId, { enabled: status.versioningEnabled });
  // Held back while the deployments load, so "nothing is released" never flashes.
  const targetsLoading = status.versioningEnabled && deployments === undefined;
  const target = versioningFlag && !targetsLoading
    ? runTargetState({
        versioningEnabled: status.versioningEnabled,
        publishedVersion: status.publishedVersion,
        environments,
        deployments: deployments ?? [],
      })
    : null;
  const choice = target?.kind === 'published' ? target.defaultChoice : null;
  const nothingReleased = target?.kind === 'published' && !choice;

  if (testRunId) {
    return (
      <Link
        to={testRunSectionPath(endpointId, testRunId)}
        data-testid="assistant-view-test-run"
        className="mt-1.5 inline-flex text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
      >
        View run →
      </Link>
    );
  }

  const start = async () => {
    try {
      const created = await createTestRun.mutateAsync({
        endpointId,
        testIds,
        environmentId: choice?.environmentId ?? null,
        concurrency: 1,
        graded: true,
        origin: 'assistant',
      });
      startedTestRuns.set(turnId, created.id);
      setTestRunId(created.id);
      navigate(testRunSectionPath(endpointId, created.id));
    } catch (err) {
      // The card cannot see the Testing tab's target picker, so it never
      // pre-disables; a scenario already running there comes back as 409
      // IN_FLIGHT and is named (task 1229). The button stays enabled.
      const names = inFlightRefusal(err);
      if (names) toast?.error(IN_FLIGHT_TOAST_TITLE, inFlightToastDetail(names));
      else toast?.error('Test failed to start', err instanceof Error ? err.message : undefined);
    }
  };

  const button = (
    <button
      type="button"
      onClick={() => void start()}
      disabled={createTestRun.isPending || nothingReleased || targetsLoading}
      title={nothingReleased ? NOTHING_RELEASED : undefined}
      data-testid="assistant-run-these-tests"
      className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
    >
      <PlayIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {createTestRun.isPending ? 'Starting…' : 'Run these scenarios'}
    </button>
  );

  // The card's confirmation names the target once there is more than one (task 1236).
  return (
    <span className="block">
      {button}
      {choice && (
        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400" data-testid="assistant-run-target">
          Runs on {choice.label}
        </span>
      )}
      {nothingReleased && (
        <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300" data-testid="assistant-run-target">
          {NOTHING_RELEASED}
        </span>
      )}
    </span>
  );
}

/** The two follow-ups the test-turn card offers (task 1204): chip label, then the turn it sends. */
export const MORE_TESTS_OPTIONS = [
  { testId: 'assistant-tests-more-like', label: '3 more like these', text: 'Add 3 more test scenarios like these' },
  { testId: 'assistant-tests-more-edge', label: '3 more edge cases', text: 'Add 3 more edge-case test scenarios' },
] as const;

/**
 * "Want more?" on the test-turn card (task 1204), rendered only when the turn
 * saved at least one test.
 *
 * A chip sends its fixed text through the provider's `send`, so the request is
 * the User's next turn in the SAME thread (never `seedFirstTurn`, which starts
 * a new one); the dispatcher routes it back to the test tier, which adds to the
 * set. Both chips are disabled while a turn is in flight, and the clicked chip
 * holds a sent state (check icon, "Sent") for `SENT_STATE_MS`, the treatment
 * the page handoff buttons use (task 1196). The chip style is the sidebar's
 * option chip (`assistant-option`).
 */
export function MoreTestsOffer() {
  const testingCardsEnabled = useFlag('innovate.fe.assistant.testingCards');
  return testingCardsEnabled ? <MoreTestsOfferContent /> : null;
}

function MoreTestsOfferContent() {
  const { send, isSending } = useAssistant();
  const [sentTestId, setSentTestId] = useState<string | null>(null);
  // A second click can land before the re-render that disables the chips.
  const sendingRef = useRef(false);

  useEffect(() => {
    if (!sentTestId) return;
    const timer = window.setTimeout(() => setSentTestId(null), SENT_STATE_MS);
    return () => window.clearTimeout(timer);
  }, [sentTestId]);

  useEffect(() => {
    if (!isSending) sendingRef.current = false;
  }, [isSending]);

  const choose = (testId: string, text: string) => {
    if (isSending || sendingRef.current) return;
    sendingRef.current = true;
    setSentTestId(testId);
    void send(text);
  };

  return (
    <div data-testid="assistant-tests-more" className="mt-2 flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-500 dark:text-gray-400">Want more?</span>
      {MORE_TESTS_OPTIONS.map((option) => {
        const sent = sentTestId === option.testId;
        return (
          <button
            key={option.testId}
            type="button"
            data-testid={option.testId}
            data-sent={sent ? 'true' : undefined}
            disabled={isSending || sent}
            onClick={() => choose(option.testId, option.text)}
            className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-white px-3 py-1.5 text-sm text-indigo-700 transition-colors hover:bg-indigo-50 disabled:opacity-50 motion-reduce:transition-none dark:border-indigo-700 dark:bg-gray-800 dark:text-indigo-300"
          >
            {sent && <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />}
            {sent ? 'Sent' : option.label}
          </button>
        );
      })}
    </div>
  );
}
